import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import {
  analyseMenu,
  gpTargetOf,
  menuItemShown,
  menuScatter,
  money,
  pricePreview,
  QUADRANT_ACTION,
  QUADRANT_LABEL,
  type MenuBasis,
  type MenuItem,
  type MenuQuadrant,
  type SalesRecord,
  clockOf,
  DEFAULT_PERIOD,
  posUnitsIn,
  serviceDate,
  periodLabel,
  windowOf,
  type Period,
  type PosUnits,
} from "@baros/core";
import { ApiError } from "../api.js";
import { useApp } from "../context.js";
import { pct, quadrantColour } from "../format.js";
import type { ScreenProps } from "../screens.js";
import { menuApi } from "./api.js";
import { salesApi } from "../sales/api.js";
import { PeriodPicker } from "../Period.js";
import "./menu.css";

/**
 * Screen 07 — menu engineering.
 *
 * Fig. 7.1 from the prototype: popularity against margin, and every label
 * placed so it does not sit on another label or another drink. A drink is
 * popular at 70% of an equal share of the menu's units and profitable at the
 * venue's gross profit target — not at the menu's means, which called half
 * of any list a problem. What the screen leads with is dollars: what each
 * drink brings in over a week, and what being under target costs.
 *
 * There is no arithmetic in this file. Costs, margins, quadrants, pixel
 * positions and label boxes all come from `@baros/core`'s `menu.ts`, so the
 * figure a manager reads here is the figure the API would compute — the
 * agreement rule 4 of the brief asks for.
 */

type SortKey = "name" | "price" | "cost" | "gp" | "units" | "margin" | "weekly" | "quadrant";

const QUADRANT_ORDER: readonly MenuQuadrant[] = ["star", "plough-horse", "puzzle", "dog"];

const QUADRANT_NOTE: Readonly<Record<MenuQuadrant, string>> = {
  star: "Sells, on target · protect these",
  "plough-horse": "Sells, under target · reprice or recost",
  puzzle: "On target, slow · sell them harder",
  dog: "Slow, under target · cut or rework",
};

/** Where the four corner captions go, given the plot box. */
const CORNERS: readonly { readonly q: MenuQuadrant; readonly dx: number; readonly dy: number; readonly anchor: "start" | "end" }[] = [
  { q: "puzzle", dx: 10, dy: 20, anchor: "start" },
  { q: "star", dx: -10, dy: 20, anchor: "end" },
  { q: "dog", dx: 10, dy: -10, anchor: "start" },
  { q: "plough-horse", dx: -10, dy: -10, anchor: "end" },
];

export function MenuScreen({ params }: ScreenProps) {
  const app = useApp();
  const { token, venueId } = app;

  const [sales, setSales] = useState<readonly SalesRecord[] | null>(null);
  /**
   * The nights the figures cover (#169). The stored `SALES#` rows are a
   * trailing four weeks, or whatever a manager typed in; with a period
   * chosen the units come from the till's own nights instead, so the same
   * scatter can be read over a week, a month or a span somebody picked.
   */
  const [period, setPeriod] = useState<Period>(DEFAULT_PERIOD);
  const [windowUnits, setWindowUnits] = useState<readonly PosUnits[] | null>(null);
  /** Null until the first read says whether there is a till to read at all. */
  const [tillLinked, setTillLinked] = useState<boolean | null>(null);
  // GP % first: it is the axis the venue's target is a line on.
  const [basis, setBasis] = useState<MenuBasis>("gp");
  const [selected, setSelected] = useState<string | null>(params.specId ?? null);
  // The drink under the microscope is part of the address.
  const { go } = app;
  useEffect(() => {
    go({ kind: "menu", params: selected ? { specId: selected } : {} }, { replace: true });
  }, [selected, go]);
  const [sort, setSort] = useState<{ key: SortKey; desc: boolean }>({ key: "weekly", desc: true });
  // The categories showing, on the plot and in the table alike. Drinks that
  // sold nothing start off: there are usually a lot of them and they say
  // nothing about what sells.
  const [quadrants, setQuadrants] = useState<readonly MenuQuadrant[]>(QUADRANT_ORDER);
  const [unsoldOn, setUnsoldOn] = useState(false);
  // The dot under the pointer, whose name shows while it is there.
  const [hovered, setHovered] = useState<string | null>(null);
  const [testPrice, setTestPrice] = useState<number | null>(null);
  const [draftUnits, setDraftUnits] = useState<Record<string, string>>({});
  const [savingId, setSavingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const clock = clockOf(app.venueState?.venue);
  const nights = useMemo(
    () => windowOf(period, clock, new Date()),
    [period, clock.timeZone, clock.rolloverHour, clock.weekStartsOn],
  );

  useEffect(() => {
    if (!venueId) {
      setSales([]);
      return;
    }
    let live = true;
    menuApi
      .sales(token, venueId)
      .then((r) => {
        if (live) setSales(r.sales);
      })
      .catch((err: unknown) => {
        if (!live) return;
        setSales([]);
        setError(err instanceof ApiError ? err.message : "Could not load the sales figures");
      });
    return () => {
      live = false;
    };
  }, [token, venueId]);

  // The till's nights for the window. A period that runs past tonight is
  // clipped here rather than in `posUnitsIn`: the rate divides by the window,
  // so asking for October on the third would otherwise read every drink at a
  // quarter of what it is actually selling.
  useEffect(() => {
    if (!venueId) return;
    let live = true;
    const today = serviceDate(new Date(), { timeZone: clock });
    const to = nights.to > today ? today : nights.to;
    salesApi
      .days(token, venueId, { from: nights.from, to })
      .then((r) => {
        if (!live) return;
        setTillLinked(r.linked);
        setWindowUnits(r.linked ? posUnitsIn(r.days, { from: nights.from, to }) : null);
      })
      .catch(() => {
        // The stored figures are still on screen; a till that cannot be read
        // is not a reason to empty the scatter.
        if (live) setTillLinked(false);
      });
    return () => {
      live = false;
    };
  }, [token, venueId, nights.from, nights.to, clock.timeZone]);

  /**
   * `/v1/me` answers this with the same `can()` the route enforces with, so
   * the editor is offered exactly where a PUT would be allowed. The server
   * checks again per venue; this only decides what is on screen.
   */
  const canEdit = app.me.can["sales:edit"];

  const ingredientsById = useMemo(
    () => new Map(app.library.ingredients.map((i) => [i.ingredientId, i])),
    [app.library.ingredients],
  );
  // The venue's own target (Organisation › Venues); 74% until it sets one.
  const gpTarget = gpTargetOf(app.venueState?.venue);
  const salesBySpecId = useMemo(() => {
    const stored = new Map((sales ?? []).map((s) => [s.specId, s]));
    if (!windowUnits) return stored;
    // The window's own units, over the stored row's, keeping everything else
    // the row carries. A spec the till did not sell in the window reads as
    // nought rather than keeping last month's number.
    const windowed = new Map(stored);
    for (const [specId, row] of stored) {
      windowed.set(specId, { ...row, unitsPerWeek: 0, source: "pos", periodStart: nights.from });
    }
    for (const u of windowUnits) {
      const row = stored.get(u.specId);
      windowed.set(u.specId, {
        ...(row ?? { specId: u.specId, source: "pos" as const }),
        specId: u.specId,
        unitsPerWeek: u.unitsPerWeek,
        source: "pos",
        periodStart: u.periodStart,
      } as SalesRecord);
    }
    return windowed;
  }, [sales, windowUnits, nights.from]);

  const analysis = useMemo(
    () =>
      analyseMenu({
        specs: app.library.specs,
        ingredientsById,
        overridesBySpecId: app.overrides,
        salesBySpecId,
        basis,
        gpTarget,
      }),
    [app.library.specs, ingredientsById, app.overrides, salesBySpecId, basis, gpTarget],
  );

  // With no sales at all there is nothing else to show, so they are on.
  const shown = useMemo(
    () => ({ quadrants, unsold: unsoldOn || analysis.totalUnits === 0 }),
    [quadrants, unsoldOn, analysis.totalUnits],
  );

  const scatter = useMemo(
    () => menuScatter(analysis, { selectedSpecId: selected, ...shown }),
    [analysis, selected, shown],
  );

  const current: MenuItem | undefined = useMemo(
    () => analysis.items.find((i) => i.specId === selected),
    [analysis.items, selected],
  );

  // A new selection resets the price probe to what is actually on the menu.
  useEffect(() => {
    setTestPrice(current ? current.price : null);
  }, [current]);

  const preview = useMemo(
    () => (current && testPrice !== null ? pricePreview(current, testPrice, analysis) : null),
    [current, testPrice, analysis],
  );

  const ghost = useMemo(() => {
    if (!current || !preview || preview.unchanged) return null;
    const from = scatter.project(current.unitsPerWeek, current.value);
    const to = scatter.project(
      current.unitsPerWeek,
      basis === "gp" ? preview.gp : preview.contributionMargin,
    );
    return { from, to };
  }, [current, preview, scatter, basis]);

  const saveUnits = useCallback(
    async (specId: string, raw: string) => {
      if (!venueId) return;
      const value = Number(raw);
      if (!Number.isFinite(value) || value < 0) {
        setError("Units per week must be zero or more");
        return;
      }
      setSavingId(specId);
      setError(null);
      try {
        const { sale } = await menuApi.saveSales(token, venueId, specId, {
          unitsPerWeek: value,
          source: "manual",
        });
        setSales((prev) => [...(prev ?? []).filter((s) => s.specId !== specId), sale]);
        setDraftUnits((d) => {
          const next = { ...d };
          delete next[specId];
          return next;
        });
      } catch (err) {
        setError(err instanceof ApiError ? err.message : "Could not save that figure");
      } finally {
        setSavingId(null);
      }
    },
    [token, venueId],
  );

  const rows = useMemo(() => {
    const of = (item: MenuItem): number | string => {
      switch (sort.key) {
        case "name":
          return item.name;
        case "price":
          return item.price;
        case "cost":
          return item.cost;
        case "gp":
          return item.gp;
        case "units":
          return item.unitsPerWeek;
        case "margin":
          return item.contributionMargin;
        case "weekly":
          return item.weeklyMargin;
        case "quadrant":
          return QUADRANT_ORDER.indexOf(item.quadrant);
      }
    };
    const sorted = analysis.items
      .filter((i) => menuItemShown(i, shown))
      .slice()
      .sort((a, b) => {
        const va = of(a);
        const vb = of(b);
        const c =
          typeof va === "string" && typeof vb === "string"
            ? va.localeCompare(vb)
            : Number(va) - Number(vb);
        return (sort.desc ? -c : c) || a.name.localeCompare(b.name);
      });
    // What sold, then what did not: a drink nobody ordered has no place in a
    // ranking of what the list earns, whichever column it is ranked on.
    return [...sorted.filter((i) => i.unitsPerWeek > 0), ...sorted.filter((i) => i.unitsPerWeek === 0)];
  }, [analysis.items, sort, shown]);
  const sellingRows = rows.filter((i) => i.unitsPerWeek > 0).length;
  const topShare = Math.max(0, ...analysis.items.map((i) => i.share));

  if (!venueId) {
    return (
      <div className="pad">
        <div className="msg err">
          You are not assigned to a venue, so there is no menu to engineer. Price and sales are
          both venue-scoped.
        </div>
      </div>
    );
  }

  const basisLabel = basis === "gp" ? "gross profit %" : "contribution margin";
  const valueOf = (v: number) => (basis === "gp" ? pct(v) : money(v));

  const header = (key: SortKey, label: string, className: string) => {
    const active = sort.key === key;
    return (
      <span className={className}>
        <button
          aria-sort={active ? (sort.desc ? "descending" : "ascending") : "none"}
          onClick={() =>
            setSort((s) => (s.key === key ? { key, desc: !s.desc } : { key, desc: key !== "name" }))
          }
        >
          {label}
          {active ? (sort.desc ? " ↓" : " ↑") : ""}
        </button>
      </span>
    );
  };

  return (
    <div className="split">
      <section className="pane">
        <div className="menu-head">
          <div>
            <div className="label">
              Popularity against {basisLabel} · popular from{" "}
              {analysis.popularUnits.toFixed(1)} a week · GP target {pct(analysis.gpTarget)}
            </div>
            <div style={{ fontSize: 22, fontWeight: 600, marginTop: 4 }}>
              {analysis.items.length} drinks on list
            </div>
            <div className="label" style={{ marginTop: 4 }}>
              {tillLinked === false
                ? "Units as recorded — this venue has no till linked, so a period would change nothing."
                : `Units from the till · ${periodLabel(period, nights).toLowerCase()} · ${nights.from} to ${nights.to}`}
            </div>
          </div>
          <span className="spacer" />
          {/* Offered only where the till can answer it. With no sales read,
              the figures are whatever a manager typed in and they are not
              a period's. */}
          {tillLinked !== false && (
            <PeriodPicker
              period={period}
              shown={nights}
              onChange={setPeriod}
              label="Which nights the units cover"
            />
          )}
          <div className="row">
            <span className="label">Profit axis</span>
            <button className="btn" aria-pressed={basis === "margin"} onClick={() => setBasis("margin")}>
              Margin $
            </button>
            <button className="btn" aria-pressed={basis === "gp"} onClick={() => setBasis("gp")}>
              GP %
            </button>
          </div>
        </div>

        {error && (
          <div style={{ padding: "0 20px 10px" }}>
            <div className="msg err">{error}</div>
          </div>
        )}

        <div className="scroll">
          {analysis.items.length > 0 && (
            <div className="menu-summary">
              <div className="menu-stat">
                <span className="label">Margin a week</span>
                <span className="q-n">{money(analysis.totalWeeklyMargin)}</span>
                <span className="q-note">{analysis.totalUnits} drinks sold</span>
              </div>
              <div className="menu-stat">
                <span className="label">Menu GP</span>
                <span className="q-n">{analysis.totalUnits > 0 ? pct(analysis.weightedGp) : "—"}</span>
                <span className="q-note">Weighted by what sells · target {pct(analysis.gpTarget)}</span>
              </div>
              <div className="menu-stat">
                <span className="label">Under target</span>
                <span className="q-n">{analysis.items.filter((i) => i.belowTarget).length}</span>
                <span className="q-note">
                  {analysis.totalWeeklyShortfall > 0
                    ? `${money(analysis.totalWeeklyShortfall)} a week short`
                    : "Nothing lost to it"}
                </span>
              </div>
              <div className="menu-stat">
                <span className="label">Not selling</span>
                <span className="q-n">{analysis.unsoldCount}</span>
                <span className="q-note">Of {analysis.items.length} on the list</span>
              </div>
            </div>
          )}
          {analysis.items.length > 0 && (
            <div className="menu-pills" role="group" aria-label="Categories showing">
              <span className="label">Showing</span>
              {QUADRANT_ORDER.map((q) => {
                const on = quadrants.includes(q);
                return (
                  <button
                    key={q}
                    className="menu-pill"
                    aria-pressed={on}
                    style={{ ["--c" as string]: quadrantColour(q) }}
                    onClick={() => setQuadrants((now) => (now.includes(q) ? now.filter((x) => x !== q) : [...now, q]))}
                  >
                    <span className="dot" />
                    {QUADRANT_LABEL[q]} <span className="n">{analysis.counts[q]}</span>
                  </button>
                );
              })}
              <button
                className="menu-pill"
                aria-pressed={shown.unsold}
                disabled={analysis.totalUnits === 0 || analysis.unsoldCount === 0}
                onClick={() => setUnsoldOn((now) => !now)}
              >
                Not selling <span className="n">{analysis.unsoldCount}</span>
              </button>
            </div>
          )}
          <div className="menu-plot-wrap">
            {sales === null ? (
              <div className="pad label">Loading the sales figures…</div>
            ) : analysis.items.length === 0 ? (
              <div className="pad label">
                Nothing is priced and available at this venue, so there is nothing to plot.
              </div>
            ) : (
              <svg
                className="menu-plot"
                width={scatter.width}
                height={scatter.height}
                viewBox={`0 0 ${scatter.width} ${scatter.height}`}
                role="img"
                aria-label={`Menu engineering quadrant plot: ${analysis.items.length} drinks, popularity against ${basisLabel}`}
              >
                <rect
                  className="menu-frame"
                  x={scatter.plot.x0}
                  y={scatter.plot.y0}
                  width={scatter.plot.x1 - scatter.plot.x0}
                  height={scatter.plot.y1 - scatter.plot.y0}
                />
                <path
                  className="menu-divider"
                  d={
                    `M${scatter.popularX},${scatter.plot.y0} L${scatter.popularX},${scatter.plot.y1}` +
                    (scatter.targetY === null
                      ? ""
                      : ` M${scatter.plot.x0},${scatter.targetY} L${scatter.plot.x1},${scatter.targetY}`)
                  }
                />

                {CORNERS.map((c) => (
                  <text
                    key={c.q}
                    className="menu-corner"
                    x={c.dx > 0 ? scatter.plot.x0 + c.dx : scatter.plot.x1 + c.dx}
                    y={c.dy > 0 ? scatter.plot.y0 + c.dy : scatter.plot.y1 + c.dy}
                    textAnchor={c.anchor}
                  >
                    {QUADRANT_ACTION[c.q]}
                  </text>
                ))}

                {scatter.yTicks.map((t) => (
                  <text key={`y${t.value}`} className="menu-axis" x={scatter.plot.x0 - 8} y={t.at + 4} textAnchor="end">
                    {basis === "gp" ? `${t.value.toFixed(0)}%` : money(t.value)}
                  </text>
                ))}
                {scatter.xTicks.map((t) => (
                  <text key={`x${t.value}`} className="menu-axis" x={t.at} y={scatter.plot.y1 + 20} textAnchor="middle">
                    {t.value.toFixed(0)}
                  </text>
                ))}
                <text
                  className="menu-axis-title"
                  x={(scatter.plot.x0 + scatter.plot.x1) / 2}
                  y={scatter.plot.y1 + 40}
                  textAnchor="middle"
                >
                  Units per week
                </text>
                <text
                  className="menu-axis-title"
                  x={26}
                  y={(scatter.plot.y0 + scatter.plot.y1) / 2}
                  textAnchor="middle"
                  transform={`rotate(-90 26 ${(scatter.plot.y0 + scatter.plot.y1) / 2})`}
                >
                  {basis === "gp" ? "Gross profit %" : "Margin per serve"}
                </text>

                {ghost && (
                  <g className="menu-ghost">
                    <path d={`M${ghost.from.x},${ghost.from.y} L${ghost.to.x},${ghost.to.y}`} />
                    <circle cx={ghost.to.x} cy={ghost.to.y} r={5} />
                  </g>
                )}

                {scatter.points.map((p) => (
                  <g
                    key={p.specId}
                    className="menu-point"
                    data-selected={p.selected}
                    data-estimated={analysis.items.find((i) => i.specId === p.specId)?.estimated}
                    role="button"
                    tabIndex={0}
                    aria-label={`${p.name}, ${p.unitsPerWeek} a week, ${valueOf(p.value)}, ${QUADRANT_LABEL[p.quadrant]}`}
                    onClick={() => setSelected(p.specId)}
                    onMouseEnter={() => setHovered(p.specId)}
                    onMouseLeave={() => setHovered((now) => (now === p.specId ? null : now))}
                    onFocus={() => setHovered(p.specId)}
                    onBlur={() => setHovered((now) => (now === p.specId ? null : now))}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setSelected(p.specId);
                      }
                    }}
                  >
                    <circle
                      cx={p.x}
                      cy={p.y}
                      r={p.r}
                      style={p.selected ? undefined : { fill: quadrantColour(p.quadrant) }}
                    />
                    {p.named && (
                      <foreignObject
                        x={p.label.x}
                        y={p.label.y}
                        width={p.label.width}
                        height={18}
                        style={{ pointerEvents: "none", overflow: "visible" }}
                      >
                        <div className={`menu-label${p.label.anchor === "end" ? " end" : ""}`}>{p.name}</div>
                      </foreignObject>
                    )}
                  </g>
                ))}

                {/* The name of an unnamed drink while it is pointed at: drawn
                    last, so no other dot or name sits on top of it. */}
                {scatter.points
                  .filter((p) => p.specId === hovered && !p.named)
                  .map((p) => (
                    <foreignObject
                      key={p.specId}
                      x={p.label.x}
                      y={p.label.y}
                      width={p.label.width}
                      height={18}
                      style={{ pointerEvents: "none", overflow: "visible" }}
                    >
                      <div className={`menu-label hover${p.label.anchor === "end" ? " end" : ""}`}>
                        <span>{p.name}</span>
                      </div>
                    </foreignObject>
                  ))}
              </svg>
            )}
          </div>

          {analysis.items.length > 0 && sales !== null && (
            <div className="menu-plot-note label">
              A bigger dot makes more in a week · the biggest earners are named, point at or tap any other
              {scatter.unplottedCount > 0 ? ` · ${scatter.unplottedCount} of ${analysis.items.length} drinks hidden` : ""}
            </div>
          )}

          <div className="menu-quadrants">
            {QUADRANT_ORDER.map((q) => (
              <div key={q} className="menu-quadrant">
                <span className="label" style={{ color: quadrantColour(q) }}>
                  {QUADRANT_LABEL[q]} · {QUADRANT_ACTION[q]}
                </span>
                <span className="q-n">{analysis.counts[q]}</span>
                <span className="q-note">{QUADRANT_NOTE[q]}</span>
              </div>
            ))}
          </div>

          <div style={{ padding: "0 20px 20px" }}>
            <div className="table menu-table">
              <div className="thead">
                {header("name", "Drink", "m-name")}
                {header("price", "Price", "m-num")}
                {header("cost", "Cost", "m-num")}
                {header("gp", "GP", "m-num")}
                {header("units", "Units / wk", "m-units")}
                {header("margin", "Margin", "m-num")}
                {header("weekly", "$ / wk", "m-num")}
                {header("quadrant", "Quadrant", "m-quad")}
              </div>
              {rows.map((item, index) => (
                <Fragment key={item.specId}>
                {index === sellingRows && (
                  <div className="menu-group label">
                    Sold nothing in the period · {rows.length - sellingRows}
                  </div>
                )}
                <div
                  className="trow"
                  aria-current={item.specId === selected}
                >
                  <button
                    className="menu-row-open"
                    onClick={() => setSelected(item.specId)}
                    title="Show this drink in the panel"
                  >
                    <span className="m-name">
                      <span className="n">{item.name}</span>
                      <span className="s">
                        {item.style.join(" · ") || "—"}
                        {item.estimated ? " · no sales recorded" : ""}
                        {item.weeklyShortfall > 0 ? ` · ${money(item.weeklyShortfall)} a week under target` : ""}
                      </span>
                    </span>
                    <span className="m-num">{money(item.price)}</span>
                    <span className="m-num dim">
                      {money(item.cost)}
                      {item.unknownIngredientIds.length ? "*" : ""}
                    </span>
                    <span className="m-num" data-below={item.belowTarget}>{pct(item.gp)}</span>
                  </button>
                  <span className="m-units">
                    {canEdit ? (
                      <input
                        type="number"
                        min={0}
                        step={1}
                        aria-label={`Units per week for ${item.name}`}
                        disabled={savingId === item.specId}
                        value={draftUnits[item.specId] ?? String(item.unitsPerWeek)}
                        onChange={(e) =>
                          setDraftUnits((d) => ({ ...d, [item.specId]: e.target.value }))
                        }
                        onBlur={(e) => {
                          const raw = e.target.value;
                          if (raw === "" || Number(raw) === item.unitsPerWeek) {
                            setDraftUnits((d) => {
                              const next = { ...d };
                              delete next[item.specId];
                              return next;
                            });
                            return;
                          }
                          void saveUnits(item.specId, raw);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") e.currentTarget.blur();
                          if (e.key === "Escape") {
                            setDraftUnits((d) => {
                              const next = { ...d };
                              delete next[item.specId];
                              return next;
                            });
                          }
                        }}
                      />
                    ) : (
                      <span className="num">{item.unitsPerWeek}</span>
                    )}
                  </span>
                  <button className="m-num" onClick={() => setSelected(item.specId)}>
                    {money(item.contributionMargin)}
                  </button>
                  <button
                    className="m-num m-weekly"
                    title={`${pct(item.share * 100)} of the menu's weekly margin`}
                    onClick={() => setSelected(item.specId)}
                  >
                    {money(item.weeklyMargin)}
                    {/* Its part of the week's margin, against the biggest earner. */}
                    {item.share > 0 && (
                      <span className="bar">
                        <span style={{ width: `${Math.max(3, (item.share / topShare) * 100)}%`, background: quadrantColour(item.quadrant) }} />
                      </span>
                    )}
                  </button>
                  <button
                    className="m-quad"
                    style={{ color: quadrantColour(item.quadrant) }}
                    onClick={() => setSelected(item.specId)}
                  >
                    {QUADRANT_LABEL[item.quadrant]}
                  </button>
                </div>
                </Fragment>
              ))}
              {rows.length === 0 && <div className="pad label">Nothing in the categories showing.</div>}
            </div>
            {analysis.unpricedSpecIds.length > 0 && (
              <div className="label" style={{ paddingTop: 10, lineHeight: 1.6 }}>
                {analysis.unpricedSpecIds.length} available{" "}
                {analysis.unpricedSpecIds.length === 1 ? "drink has" : "drinks have"} no price at
                this venue and cannot be plotted. Set one on the spec.
              </div>
            )}
          </div>
        </div>
      </section>

      <aside className="aside" style={{ background: "var(--sf)" }}>
        <div className="menu-aside-head">
          <div className="label">Selected</div>
          <div className="menu-aside-name">{current?.name ?? "—"}</div>
          <div className="label" style={{ marginTop: 6 }}>
            {current ? current.style.join(" · ") || "—" : "Tap a point or a row"}
          </div>
        </div>

        <div className="scroll" style={{ padding: 16 }}>
          {current ? (
            <>
              <div className="kv">
                <span className="k">Quadrant</span>
                <span className="v" style={{ color: quadrantColour(current.quadrant) }}>
                  {QUADRANT_LABEL[current.quadrant]} · {QUADRANT_ACTION[current.quadrant]}
                </span>
              </div>
              <div className="kv">
                <span className="k">Menu price</span>
                <span className="v">{money(current.price)}</span>
              </div>
              <div className="kv">
                <span className="k">Cost / serve</span>
                <span className="v">{money(current.cost)}</span>
              </div>
              <div className="kv">
                <span className="k">Pour cost</span>
                <span className="v">{pct(current.pourCost)}</span>
              </div>
              <div className="kv">
                <span className="k">Gross profit</span>
                <span className="v">{pct(current.gp)}</span>
              </div>
              <div className="kv">
                <span className="k">Margin / serve</span>
                <span className="v">{money(current.contributionMargin)}</span>
              </div>
              <div className="kv">
                <span className="k">Units / week</span>
                <span className="v">
                  {current.unitsPerWeek}
                  {current.estimated ? " (none recorded)" : ""}
                </span>
              </div>
              <div className="kv">
                <span className="k">Margin / week</span>
                <span className="v">{money(current.weeklyMargin)}</span>
              </div>
              <div className="kv">
                <span className="k">Share of margin</span>
                <span className="v">{pct(current.share * 100)}</span>
              </div>
              <div className="kv">
                <span className="k">Against {pct(analysis.gpTarget)} target</span>
                <span className="v" data-below={current.belowTarget}>
                  {!current.belowTarget
                    ? "On target"
                    : current.weeklyShortfall > 0
                      ? `${money(current.weeklyShortfall)} a week short`
                      : "Under"}
                </span>
              </div>
              <div className="kv">
                <span className="k">Popular from</span>
                <span className="v">{analysis.popularUnits.toFixed(1)} / wk</span>
              </div>
            </>
          ) : (
            <div className="label" style={{ lineHeight: 1.7 }}>
              A drink is popular when it sells at least 70% of an equal share of the menu, and
              profitable when it makes the venue&rsquo;s {pct(analysis.gpTarget)} gross profit
              target. Tap one to see its figures and test a price against them.
            </div>
          )}
        </div>

        {current && testPrice !== null && (
          <div className="menu-probe">
            <div className="label">Test a price · menu is GST inclusive</div>
            <div className="menu-probe-row">
              <button
                aria-label="Lower the test price"
                onClick={() => setTestPrice((p) => Math.max(0.5, (p ?? current.price) - 0.5))}
              >
                −
              </button>
              <div className="menu-probe-price">{money(testPrice)}</div>
              <button
                aria-label="Raise the test price"
                onClick={() => setTestPrice((p) => Math.min(500, (p ?? current.price) + 0.5))}
              >
                +
              </button>
            </div>
            <div className="menu-probe-effect">
              {preview && !preview.unchanged
                ? `GP ${pct(current.gp)} → ${pct(preview.gp)} · pour cost ${pct(preview.pourCost)} · ` +
                  `margin per week ${money(preview.weeklyMargin)} (${preview.weeklyMarginDelta >= 0 ? "+" : ""}${money(preview.weeklyMarginDelta)}) · ` +
                  `${QUADRANT_LABEL[preview.quadrant]}`
                : "At the current price. Move it to see the point travel. Volume is held constant — a rise usually costs some units."}
            </div>
            <div className="row" style={{ gap: 10 }}>
              <button
                className="btn primary"
                style={{ flex: 1, justifyContent: "center" }}
                onClick={() => app.go({ kind: "recipes", params: { specId: current.specId } })}
              >
                Open spec
              </button>
              <button
                className="btn"
                style={{ height: 56 }}
                onClick={() => setTestPrice(current.price)}
                disabled={preview?.unchanged ?? true}
              >
                Reset
              </button>
            </div>
          </div>
        )}
      </aside>
    </div>
  );
}
